import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Formik, FormikProps } from 'formik';
import moment from 'moment';
import cx from 'classnames';

// components
import Dialog from 'shared/components/Dialog';
import ButtonCross from 'shared/components/Buttons/ButtonCross';
import DonationItemForm from '../../DonationForms/DonationItemForm';
import ButtonOutlined from 'shared/components/Buttons/ButtonOutlined';

// hooks
import { useCreateDonationItem, useEditDonationItem, usePublishDonationItem } from 'hooks/store';

// helpers
import { sendFilesToS3Bucket } from 'core/functions';
import { prepareImages } from 'shared/components/SortableImagesListField';
import { DONATION_TYPE } from '@joc/api-gateway/lib/api-client';

// validation
import { donationItemValidationSchema, DonationTypeEnum } from '../../helpers';

// types
import { IDonationResponse, IUpdateDonationRequest } from '@joc/api-gateway';
import { DonationItemFormType, DonationItemResponseType } from '../../types';

import styles from './DonationItemPopup.module.scss';

const EMPTY_INITIAL_VALUES = {
	title: '',
	subtitle: '',
	description: '',
	productUrl: '',
	mediaPathes: [],
	price: null,
	expirationDate: null,
	countryIds: [],
} as unknown as DonationItemFormType;

type DonationItemPopupProps = {
	donationItem?: DonationItemResponseType;
	open?: boolean;
	onClose?: () => void;
};

const DonationItemPopup: FC<DonationItemPopupProps> = ({ donationItem, open = true, onClose }) => {
	const { createDonationItem } = useCreateDonationItem();
	const { editDonationItemAsync } = useEditDonationItem();
	const { publishDonationItemAsync } = usePublishDonationItem();
	const [isSaving, setIsSaving] = useState(false);
	const [donationType, setDonationType] = useState<DONATION_TYPE>(
		() => donationItem?.donationType ?? DONATION_TYPE.DEDICATED
	);
	const formikRef = useRef<FormikProps<DonationItemFormType>>(null);
	const isInitialDonationTypeMount = useRef(true);

	const isCompleted = useMemo(() => {
		if (!donationItem?.expirationDate) return false;
		return moment(donationItem.expirationDate).isBefore(moment());
	}, [donationItem]);

	const initialValues: DonationItemFormType = useMemo(() => {
		if (donationItem) {
			return {
				...donationItem,
				price:
					donationItem.donationType === DONATION_TYPE.CAMPAIGN
						? donationItem.goal
						: donationItem.suggestedCoinAmount,
				mediaPathes: prepareImages(donationItem.mediaPathes),
			} as unknown as DonationItemFormType;
		}

		return EMPTY_INITIAL_VALUES;
	}, [donationItem]);

	const validationSchema = useMemo(
		() => donationItemValidationSchema(donationType, donationItem?.coinAmount),
		[donationType, donationItem?.coinAmount]
	);

	useEffect(() => {
		if (isInitialDonationTypeMount.current) {
			isInitialDonationTypeMount.current = false;
			return;
		}

		formikRef.current?.resetForm({ values: EMPTY_INITIAL_VALUES });
	}, [donationType]);

	const handleSubmitClickHandler = async (values: DonationItemFormType) => {
		setIsSaving(true);
		try {
			const { mediaPathes, price, expirationDate, ...body } = values;

			const uploadedImages = await Promise.all(
				mediaPathes.map(async image => {
					if (image.file) {
						const [imageUrl] = await sendFilesToS3Bucket([image.file]);

						return imageUrl;
					}

					return Promise.resolve(image.src);
				})
			);

			const adjustDate = (selectedDate?: Date) => {
				const today = moment().startOf('day'); // Today at 00:00:00
				const selectedDateMoment = moment(selectedDate);

				if (selectedDate) {
					if (selectedDateMoment.isSame(today, 'day')) {
						return selectedDateMoment.endOf('day').toDate(); // Set to 23:59:59 if it's today
					}
					return selectedDateMoment.toDate();
				}
				return null;
			};

			const adjustedExpirationDate = adjustDate(expirationDate);
			const wasExpired = donationItem?.expirationDate
				? moment(donationItem.expirationDate).isBefore(moment())
				: false;
			const isMovedToFuture = adjustedExpirationDate ? moment(adjustedExpirationDate).isAfter(moment()) : false;

			if (donationItem) {
				const updated = await editDonationItemAsync({
					id: donationItem.id,
					donationItem: {
						...body,
						...(donationType === DONATION_TYPE.CAMPAIGN
							? { goal: price ? Number(price) : 0, suggestedCoinAmount: null, donationType }
							: { suggestedCoinAmount: price ? Number(price) : 0, goal: null, donationType }),
						expirationDate: adjustedExpirationDate,
						mediaPathes: uploadedImages,
					} as unknown as IUpdateDonationRequest,
				});

				if (wasExpired && isMovedToFuture && !updated?.isPublished) {
					await publishDonationItemAsync({ id: donationItem.id });
				}
			} else {
				createDonationItem({
					...body,
					...(donationType === DONATION_TYPE.CAMPAIGN
						? { goal: price ? Number(price) : 0, suggestedCoinAmount: null, donationType }
						: { suggestedCoinAmount: price ? Number(price) : 0, goal: null, donationType }),
					isPublished: false,
					expirationDate: adjustedExpirationDate,
					mediaPathes: uploadedImages,
				} as unknown as IDonationResponse);
			}

			closePopupClickHandler();
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
		} finally {
			setIsSaving(false);
		}
	};

	const closePopupClickHandler = () => onClose?.();

	return (
		<Dialog open={open} maxWidth="xl" isFullScreen={true}>
			<section className={styles.donationItemPopup}>
				<span className={styles.donationItemPopup__title}>{donationItem ? 'Edit' : 'Create'} Donation</span>
				<ButtonCross clickHandler={closePopupClickHandler} parentClassName={styles.donationItemPopup__close} />
				<div className={styles.donationItemPopup__switch}>
					<ButtonOutlined
						title={DonationTypeEnum.DedicatedDonation}
						disabled={!!donationItem}
						parentClassName={cx(styles.donationItemPopup__switch_button, {
							[styles.donationItemPopup__switch_button_active]: donationType === DONATION_TYPE.DEDICATED,
							[styles.donationItemPopup__switch_button_disabled]: !!donationItem,
						})}
						clickHandler={() => setDonationType(DONATION_TYPE.DEDICATED)}
					/>
					<ButtonOutlined
						title={DonationTypeEnum.CampaignDonation}
						disabled={!!donationItem}
						parentClassName={cx(styles.donationItemPopup__switch_button, {
							[styles.donationItemPopup__switch_button_active]: donationType === DONATION_TYPE.CAMPAIGN,
							[styles.donationItemPopup__switch_button_disabled]: !!donationItem,
						})}
						clickHandler={() => setDonationType(DONATION_TYPE.CAMPAIGN)}
					/>
				</div>
				<Formik
					innerRef={formikRef}
					enableReinitialize={true}
					initialValues={initialValues}
					validationSchema={validationSchema}
					onSubmit={handleSubmitClickHandler}
				>
					<DonationItemForm
						isNew={!donationItem}
						isSaving={isSaving}
						isCompleted={isCompleted}
						coinAmount={donationItem?.coinAmount}
						donationType={donationType}
						onCancel={closePopupClickHandler}
					/>
				</Formik>
			</section>
		</Dialog>
	);
};
export default DonationItemPopup;
